import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import WhatsOn from '../../common/What-on';
import { getCanonicalUrl } from '../../../utils/seo';

const HERO_IMG = 'https://BelvoirCastle.b-cdn.net/Wellness/IMG_5574.webp';
const PARTICIPANT_IMG = 'https://BelvoirCastle.b-cdn.net/Wellness/Wreath%20Making%20Workshop%20-%20Participant.webp';
const BOOK_URL = '/booking-journey?locationId=978&pageId=15ccc743-b65b-4032-8a89-c376dc359198';
const WELLNESS_SITE_URL = 'https://www.belvoirwellness.com/';

const GALLERY = [
  {
    src: PARTICIPANT_IMG,
    alt: 'Sarah Youngman with a finished wreath',
  },
];

const OTHER_RETAIL_EVENTS = [
  {
    id: 'belvoir-dog-park',
    title: 'Belvoir Dog Park',
    date: 'Now open',
    description: 'A secure off-lead field beside the Retail Village. Book an hourly slot from £10 and have the field to yourselves. Free parking in the main Castle car park when you book online.',
    image: 'https://BelvoirCastlePull.b-cdn.net/Website/Dog%20Park%201.jpeg',
    location: 'Retail Village',
    link: '/dog-park',
    linkText: 'Visit Dog Park',
    price: '£10+£2 per additional dog',
  },
  {
    id: 'belvoir-wellness',
    title: 'Belvoir Wellness',
    date: 'Coming soon',
    description: 'A new wellness studio at Belvoir Castle. Register your interest and be first to hear when classes and sessions open.',
    image: 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).webp',
    location: 'Retail Village',
    link: 'https://www.belvoirwellness.com/',
    linkText: 'Visit Belvoir Wellness',
  },
  {
    id: 'meet-santas-reindeer',
    title: "Meet Santa's Reindeer",
    date: 'Saturday 19 December 2026 · 10am – 2pm',
    description: "Step into the magic of Christmas at Belvoir Retail Village and meet two of Santa's very own reindeer!",
    image: 'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/reindeer.webp',
    location: 'Retail Village',
    link: '/events/meet-santas-reindeer',
    linkText: 'View Details',
    price: 'No tickets required',
  },
  {
    id: 'santas-grotto-retail-village-2026',
    title: "Santa's Grotto — Retail Village",
    date: 'Saturday, November 21, 2026 to Thursday, December 24, 2026',
    description: 'Meet Santa in his grotto at Belvoir Retail Village. A heartwarming family visit with a present for every child — no Castle entry ticket required.',
    image: 'https://BelvoirCastle.b-cdn.net/Christmas/Santa%20Belvoir%20Retail%20Village%20-%20Image%201.webp',
    location: 'Retail Village',
    link: '/christmas/santas-grotto-retail-village-options',
    linkText: 'View Details',
    price: '£15',
  },
];

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(560px, 78vh, 820px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(560px, 82svh, 720px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 36%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 20, 40, 0.94) 0%,
    rgba(16, 29, 64, 0.55) 42%,
    rgba(16, 29, 64, 0.2) 68%,
    transparent 100%
  );
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.16;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(201, 150, 60, 0.55) 38px, rgba(201, 150, 60, 0.55) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(16, 29, 64, 0.35) 38px, rgba(16, 29, 64, 0.35) 40px);
  pointer-events: none;
  mask-image: linear-gradient(to top, black 0%, transparent 45%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.2rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);

  em {
    font-style: italic;
    color: ${C.gold};
    text-shadow: 0 1px 3px rgba(10, 20, 40, 0.75), 0 4px 16px rgba(10, 20, 40, 0.7);
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.15rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  max-width: 36rem;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.15rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => (p.$fill ? C.gold : 'rgba(16, 29, 64, 0.55)')};
  color: ${p => (p.$fill ? C.navy : '#fff')};
  border: 1px solid ${p => (p.$fill ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  backdrop-filter: blur(10px);
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const BtnGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  padding-top: ${p => (p.$tightTop ? 'clamp(1.25rem, 2.5vw, 1.75rem)' : 'clamp(3.5rem, 7vw, 5.5rem)')};
  padding-bottom: ${p => (p.$tightBottom ? 'clamp(1.25rem, 2.5vw, 1.75rem)' : 'clamp(3.5rem, 7vw, 5.5rem)')};
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? '1px solid rgba(16, 29, 64, 0.07)' : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 2px;
  background: ${C.gold};
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${C.navy};

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const SectionIntro = styled.p`
  margin: 0 0 1.25rem;
  max-width: 38rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.68);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.75rem, 4vw, 3.5rem);
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-top: clamp(1.5rem, 3vw, 2.5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: clamp(1.4rem, 2.8vw, 2rem);
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.62);
`;

const ActionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(1.25rem, 3vw, 2.5rem);
  margin-top: clamp(1.25rem, 2.5vw, 1.75rem);
  background: #fff;
  border-radius: 12px;
  padding: clamp(1.4rem, 2.8vw, 2rem);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.55rem;
  font-weight: 700;
  color: ${C.navy};
`;

const ActionText = styled.p`
  margin: 0;
  max-width: 40rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.68);
`;

const FeatureActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;

    a,
    button {
      width: 100%;
    }
  }
`;

const ActionPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
`;

const ActionOutline = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: ${C.navy};
  border: 1.5px solid rgba(16, 29, 64, 0.35);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${C.navy};
    background: ${C.navy};
    color: #fff;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 420px;
  gap: 0.55rem;

  @media (max-width: 800px) {
    grid-template-rows: 360px;
  }
`;

const GalleryItem = styled.div`
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const InfoAction = styled.button`
  background: ${C.gold};
  color: ${C.navy};
  border: none;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
  }
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(201, 150, 60, 0.08) 38px, rgba(201, 150, 60, 0.08) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(16, 29, 64, 0.2) 38px, rgba(16, 29, 64, 0.2) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.6;
`;

const CtaMeta = styled.p`
  margin: 1.5rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
`;

const WreathMakingWorkshop = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Wreath Making Workshop at Belvoir Retail Village | 28 November 2026</title>
        <meta
          name="description"
          content="Join florist Sarah Youngman from Sage & Satin Florals for wreath making at the Wellness Studio, Belvoir Retail Village, on Saturday 28 November 2026 at 1pm. £70."
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={getCanonicalUrl('whats-on/wreath-making')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Wreath Making Workshop at Belvoir Retail Village" />
        <meta
          property="og:description"
          content="Saturday 28 November 2026, 1pm at the Wellness Studio. £70, including a mince pie and mulled wine."
        />
        <meta property="og:url" content={getCanonicalUrl('whats-on/wreath-making')} />
        <meta property="og:image" content={PARTICIPANT_IMG} />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: 'Wreath Making Workshop',
            description:
              'Join florist Sarah Youngman from Sage & Satin Florals for an afternoon of wreath making upstairs in the Wellness Studio at Belvoir Retail Village.',
            image: [HERO_IMG, PARTICIPANT_IMG],
            url: getCanonicalUrl('whats-on/wreath-making'),
            startDate: '2026-11-28T13:00:00',
            endDate: '2026-11-28T15:30:00',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            maximumAttendeeCapacity: 20,
            offers: {
              '@type': 'Offer',
              price: '70',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
            },
            location: {
              '@type': 'Place',
              name: 'Wellness Studio at the Retail Village',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
            },
            organizer: {
              '@type': 'Organization',
              name: 'Belvoir Castle',
              url: 'https://www.belvoircastle.com',
            },
          })}
        </script>
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg src={HERO_IMG} alt="Greenery, twine and scissors for the wreath making workshop" />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>Wellness Studio · Saturday 28 November 2026</HeroKicker>
            <HeroTitle>
              Wreath Making <em>Workshop</em>
            </HeroTitle>
            <HeroStrapline>
              Join florist Sarah Youngman from Sage &amp; Satin Florals for an afternoon of wreath making at Belvoir.
            </HeroStrapline>
            <HeroBadgeRow>
              <Badge $fill>£70</Badge>
              <Badge>1pm start</Badge>
              <Badge>20 places</Badge>
            </HeroBadgeRow>
            <HeroButtons>
              <BtnPrimary type="button" onClick={() => navigate('/retail-village')}>
                Plan your visit
              </BtnPrimary>
              <BtnGhost
                type="button"
                onClick={() =>
                  document.getElementById('wreath-about')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                About the workshop
              </BtnGhost>
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        <Section $bg={C.ivory} $border $tightBottom id="wreath-about">
          <Inner>
            <TwoCols>
              <div>
                <Kicker>Sage &amp; Satin Florals</Kicker>
                <Divider />
                <SectionTitle>
                  An afternoon with
                  <br />
                  <em>Sarah Youngman.</em>
                </SectionTitle>
                <SectionIntro>
                  Sarah has maintained the floral installations throughout the Castle for the Her Grace, Duchess of Rutland for many years, and she knows The Estate’s gardens and woodland well.
                </SectionIntro>
                <SectionIntro>
                  In the days before the workshop, she will forage across the Belvoir estate for greenery and natural materials. She opens the session with an introduction to the nature of Belvoir and what grows here at this time of year, then guides you through building a wreath of your own.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  All materials and equipment are provided, and no experience is needed. The workshop lasts about two to two and a half hours, upstairs in the new Wellness Studio. Your ticket includes a mince pie and a mulled wine, with alcoholic and non-alcoholic options.
                </SectionIntro>
              </div>
              <GalleryGrid>
                {GALLERY.map(photo => (
                  <GalleryItem key={photo.src}>
                    <img src={photo.src} alt={photo.alt} />
                  </GalleryItem>
                ))}
              </GalleryGrid>
            </TwoCols>

            <FeatureGrid>
              <FeatureCard>
                <FeatureTitle>Foraged at Belvoir</FeatureTitle>
                <FeatureBody>
                  Greenery and natural materials gathered from the estate’s gardens and woodland in the days before you arrive.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>Everything provided</FeatureTitle>
                <FeatureBody>
                  Materials and equipment are included. No experience is needed — Sarah guides you through the wreath.
                </FeatureBody>
              </FeatureCard>
              <FeatureCard>
                <FeatureTitle>Mince pie and mulled wine</FeatureTitle>
                <FeatureBody>
                  Your ticket includes a mince pie and a mulled wine, with alcoholic and non-alcoholic options.
                </FeatureBody>
              </FeatureCard>
            </FeatureGrid>
            <ActionBox>
              <div>
                <ActionTitle>Book now</ActionTitle>
                <ActionText>
                  Limited places available. For more information about our upcoming wellness studio, register your interest.
                </ActionText>
              </div>
              <FeatureActions>
                <ActionPrimary href={BOOK_URL}>
                  Book
                </ActionPrimary>
                <ActionOutline href={WELLNESS_SITE_URL} target="_blank" rel="noopener noreferrer">
                  Belvoir Wellness
                </ActionOutline>
              </FeatureActions>
            </ActionBox>
          </Inner>
        </Section>

        <Section $bg={C.parchment} $border $tightTop>
          <Inner>
            <Kicker>Plan Your Visit</Kicker>
            <Divider />
            <SectionTitle>
              Essential <em>Information</em>
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>When</InfoLabel>
                <InfoValue>
                  Saturday
                  <br />
                  28 November 2026
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Time</InfoLabel>
                <InfoValue>
                  1pm start
                  <br />
                  2.5 hours
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Where</InfoLabel>
                <InfoValue>
                  Wellness Studio
                  <br />
                  Retail Village
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Price</InfoLabel>
                <InfoValue>£70</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Capacity</InfoLabel>
                <InfoValue>
                  20 people
                  <br />
                  Upstairs
                </InfoValue>
              </InfoItem>
              <InfoAction type="button" onClick={() => navigate('/retail-village')}>
                Plan your visit
              </InfoAction>
            </InfoGrid>
          </Inner>
        </Section>

        <WhatsOn
          title="Also at the Retail Village"
          events={OTHER_RETAIL_EVENTS}
          wheelScroll
          showParklandSection={false}
        />

        <CtaSection>
          <CtaInner>
            <CtaTitle>
              Make a wreath of your own
              <br />
              <em>from the Belvoir estate.</em>
            </CtaTitle>
            <CtaBody>
              Saturday 28 November 2026, starting at 1pm, upstairs in the Wellness Studio at Belvoir Retail Village. £70, including a mince pie and mulled wine.
            </CtaBody>
            <BtnPrimary type="button" onClick={() => navigate('/retail-village')}>
              Explore the Retail Village
            </BtnPrimary>
            <CtaMeta>
              Saturday 28 November 2026 &nbsp;·&nbsp; 1pm &nbsp;·&nbsp; Wellness Studio
            </CtaMeta>
          </CtaInner>
        </CtaSection>

        <Footer />
      </Page>
    </>
  );
};

export default WreathMakingWorkshop;
